import { SortDirection } from '@/constants/enum';
import { stringify } from '@/helpers/string';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import { IVariant } from '@/types';
import { IParamsApi } from '@/types/apis/params';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

interface IProps {
  shippingMethods?: boolean;
  products?: {
    name?: string;
    perPage?: number;
    collectionIds?: string;
    vendors?: string;
    includeProductIds?: string;
    skip?: boolean;
    sortDirection?: SortDirection;
  };
  collections?: {
    name?: string;
    perPage?: number;
    sortDirection?: SortDirection;
    includeCollectionIds?: string;
    skip?: boolean;
  };
  productTags?: {
    name?: string;
    perPage?: number;
    sortDirection?: SortDirection;
    includeProductTagsIds?: string;
    skip?: boolean;
  };
  productSKUs?: {
    name?: string;
    perPage?: number;
    sortDirection?: SortDirection;
    includeProductSKUsIds?: string;
    skip?: boolean;
  };
  inventoryLocations?: {
    name?: string;
    perPage?: number;
    sortDirection?: SortDirection;
    includeInventoryLocationIds?: string;
    skip?: boolean;
  };
  productMetafield?: {
    name?: string;
    perPage?: number;
    sortDirection?: SortDirection;
    includeProductMetafieldKey?: string;
    skip?: boolean;
  };
  variantMetafield?: {
    name?: string;
    perPage?: number;
    sortDirection?: SortDirection;
    includeVariantMetafieldKey?: string;
    skip?: boolean;
  };
  vendors?: boolean;
  areas?: boolean;
  countryRule?: boolean;
  sortDirection?: SortDirection;
}

const useOptions = (props?: IProps) => {
  const isSkip = useSelector(isSkipApiSelector);
  const { data: products, isFetching: isFetchingProducts } = apiCaller.useGetProductsQuery(
    {
      page: 1,
      perPage: props?.products?.perPage || 25,
      name: props?.products?.name,
      collectionIds: props?.products?.collectionIds,
      vendors: props?.products?.vendors,
      includeProductIds: props?.products?.includeProductIds,
      sortDirection: props?.products?.sortDirection || SortDirection.DESC,
    },
    { skip: !props?.products || !!props.products.skip || isSkip },
  );
  const [getProductsAsync] = apiCaller.useLazyGetProductsQuery();
  const { data: collections, isFetching: isFetchingCollections } = apiCaller.useGetCollectionsQuery(
    {
      page: 1,
      perPage: props?.collections?.perPage || 25,
      name: props?.collections?.name,
      sortDirection: props?.collections?.sortDirection || SortDirection.DESC,
      includeCollectionIds: props?.collections?.includeCollectionIds,
    },
    { skip: !props?.collections || !!props.collections.skip || isSkip },
  );

  const { data: productTags, isFetching: isFetchingProductTags } = apiCaller.useGetProductTagsQuery(
    {
      page: 1,
      perPage: props?.productTags?.perPage || 25,
      name: props?.productTags?.name,
      sortDirection: props?.productTags?.sortDirection || SortDirection.DESC,
      includeProductTags: props?.productTags?.includeProductTagsIds,
    },
    { skip: !props?.productTags || !!props.productTags.skip || isSkip },
  );

  const { data: productSKUs, isFetching: isFetchingSKUTags } = apiCaller.useGetProductSKUsQuery(
    {
      page: 1,
      perPage: props?.productSKUs?.perPage || 25,
      name: props?.productSKUs?.name,
      sortDirection: props?.productSKUs?.sortDirection || SortDirection.DESC,
      includeProductSKUs: props?.productSKUs?.includeProductSKUsIds,
    },
    { skip: !props?.productSKUs || !!props.productSKUs.skip || isSkip },
  );

  const { data: inventoryLocations, isFetching: isFetchingInventoryLocations } = apiCaller.useGetInventoryLocationsQuery(
    {
      page: 1,
      perPage: props?.inventoryLocations?.perPage || 25,
      name: props?.inventoryLocations?.name,
      sortDirection: props?.inventoryLocations?.sortDirection || SortDirection.DESC,
      includeLocationIds: props?.inventoryLocations?.includeInventoryLocationIds,
    },
    { skip: !props?.inventoryLocations || !!props.inventoryLocations.skip || isSkip },
  );

  const { data: vendors, isFetching: isFetchingVendors } = apiCaller.useGetVendorsQuery(undefined, {
    skip: !props?.vendors || isSkip,
  });

  const { data: productMetafield, isFetching: isFetchingProductMetafields } = apiCaller.useGetProductMetafieldQuery(
    {
      page: 1,
      perPage: props?.productMetafield?.perPage || 25,
      label: props?.productMetafield?.name,
      sortDirection: props?.productMetafield?.sortDirection || SortDirection.DESC,
      includeProductMetafieldKey: props?.productMetafield?.includeProductMetafieldKey,
    },
    { skip: !props?.productMetafield || !!props.productMetafield.skip || isSkip },
  );

  const { data: variantMetafield, isFetching: isFetchingVariantMetafields } = apiCaller.useGetVariantMetafieldQuery(
    {
      page: 1,
      perPage: props?.variantMetafield?.perPage || 25,
      label: props?.variantMetafield?.name,
      sortDirection: props?.variantMetafield?.sortDirection || SortDirection.DESC,
      includeProductMetafieldKey: props?.variantMetafield?.includeVariantMetafieldKey,
    },
    { skip: !props?.variantMetafield || !!props.variantMetafield.skip || isSkip },
  );

  const productOptions = useMemo(() => {
    if (products && products.data) {
      return products.data.map((item) => ({ value: item.product_id.toString(), label: item.title, image: item.image }));
    } else {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringify(products)]);

  const collectionOptions = useMemo(() => {
    if (collections && collections.data) {
      return collections.data.map((item) => ({ value: item.collection_id.toString(), label: item.title }));
    } else {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringify(collections)]);

  const vendorOptions = useMemo(() => {
    if (vendors && vendors.data) {
      return vendors.data.map((item) => ({ value: item, label: item }));
    } else {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringify(vendors)]);

  const variantsProducts: IParamsApi.IVariantsProduct[] = useMemo(() => {
    if (products && products.data) {
      return products.data.map(
        (item): IParamsApi.IVariantsProduct => ({
          idProduct: item.product_id.toString(),
          variants:
            JSON.parse(item.variants || '[]')?.map((item: IVariant) => ({
              id: item?.legacyResourceId?.toString(),
              name: item.title,
            })) || [],
        }),
      );
    } else {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringify(products)]);

  const productTagOptions = useMemo(() => {
    if (productTags && productTags.data) {
      return productTags.data.map((item) => ({ value: item.product_tag, label: item.product_tag }));
    } else {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringify(productTags)]);

  const productSKUOptions = useMemo(() => {
    if (productSKUs && productSKUs.data) {
      return productSKUs.data.map((item) => ({ value: item.product_sku, label: item.product_sku }));
    } else {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringify(productSKUs)]);
  const inventoryLocationOptions = useMemo(() => {
    if (inventoryLocations && inventoryLocations.data) {
      return inventoryLocations.data.map((item) => ({ value: item.shop_location_id, label: item.location_name }));
    } else {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringify(inventoryLocations)]);

  return {
    products,
    collections,
    vendors,
    isFetchingProducts,
    isFetchingCollections,
    isFetchingVendors,
    productOptions,
    collectionOptions,
    vendorOptions,
    getProductsAsync,
    variantsProducts,
    productTags,
    productTagOptions,
    productSKUs,
    productSKUOptions,
    inventoryLocations,
    inventoryLocationOptions,
    isFetchingProductTags,
    isFetchingInventoryLocations,
    isFetchingSKUTags,
    isFetchingProductMetafields,
    productMetafield,
    isFetchingVariantMetafields,
    variantMetafield,
  };
};

export default useOptions;
